import { Component, computed, inject, input } from "@angular/core";
import { GreenHomeBasisType, PeriodDefinitionType } from "@models/map/market-filter.model";
import { EVEstateFeatureType } from "src/app/ev-map/estate-map/estate-filter/search-filter-facilities/search-filter-facilities.model";
import { EVEstateType, EVOwnership, EVSiteTypeNorwegian } from "src/models/units/unit-attributes.model";
import { MarketSalesStateService } from "src/services/estate/market-sales-state.service";
import { SubscriptionFilterKeyvalueComponent } from "../subscription-filter-keyvalue/subscription-filter-keyvalue.component";
import { SubscriptionRangeInfographicsComponent } from "../subscription-range-infographics/subscription-range-infographics.component";
import { SubscriptionSelectionComponent } from "../subscription-selection/subscription-selection.component";

@Component({
    selector: "subscription-selected-filters",
    imports: [SubscriptionSelectionComponent, SubscriptionFilterKeyvalueComponent, SubscriptionRangeInfographicsComponent],
    templateUrl: "./subscription-selected-filters.component.html",
    styleUrl: "./subscription-selected-filters.component.scss",
})
export class SubscriptionSelectedFiltersComponent {
    justMapBounds = input<boolean>(false);

    private readonly marketSalesStateService = inject(MarketSalesStateService);

    filterFormAPI = this.marketSalesStateService.filterFormAPI;

    EVEstateType = EVEstateType;
    estateFeatureType = EVEstateFeatureType;
    periodType = PeriodDefinitionType;
    generatedDate: Date = new Date();

    ownershipString = computed(() => {
        const ownershipTypes = this.filterFormAPI().ownershipTypes;
        const allSelected = ownershipTypes.includes(EVOwnership.Selveier) && ownershipTypes.includes(EVOwnership.Borettslag) &&
            ownershipTypes.includes(EVOwnership.Obl_leilighet);
        if (allSelected) return "alle";
        const ownershipTypesArray: string[] = [];
        (ownershipTypes || []).forEach((t) => this.addOwnershipText(t, ownershipTypesArray));
        return ownershipTypesArray.join(", ");
    });

    plotOwnershipString = computed(() => {
        const plotOwnershipTypes = this.filterFormAPI().plotOwnershipTypes;
        return (plotOwnershipTypes || []).map((t) => EVSiteTypeNorwegian[t].toLowerCase()).join(", ");
    });

    facilitiesString = computed(() => {
        const facilitiesArray: string[] = [];
        if (this.filterFormAPI().hasBalcony) facilitiesArray.push("balkong");
        if (this.filterFormAPI().hasElevator) facilitiesArray.push("heis");
        if (this.filterFormAPI().hasParking) facilitiesArray.push("parkering");
        return facilitiesArray.join(", ");
    });

    greenHousingString = computed(() => {
        const greenHousingTypes = this.filterFormAPI().greenHomeBasisTypes;
        return (greenHousingTypes || []).map((t) => this.createGreenString(t)).join(", ");
    });

    getReportDescription(): string {
        if (this.filterFormAPI().isSitePrice) {
            return "kun tomter.";
        }
        const parts: string[] = [];
        if (this.filterFormAPI().includeJudicialTransfers) parts.push("tinglysninger");
        if (this.filterFormAPI().includeAgentSales) parts.push("meglersalg");
        if (this.filterFormAPI().estateAgentHeadOfficeId) parts.push("kun salg fra eget meglerselskap");
        if (this.filterFormAPI().isOwnedByCompany) parts.push("salg som involverer selskap (AS)");
        if (this.filterFormAPI().isForSale) parts.push("kun de som er til salgs nå");
        if (this.filterFormAPI().isWithDrawn) parts.push("trukkede eiendommer");

        let sentence = parts.length ? parts.join(", ") + "." : "";
        if (this.filterFormAPI().includeUnCertainPrices) {
            sentence += (sentence ? " " : "") + "Inkluderer spesielle omsetninger.";
        }
        return sentence;
    }

    containsEstateType(estateType: EVEstateType): boolean {
        return (this.filterFormAPI().estateTypes || [])?.includes(estateType);
    }

  private addOwnershipText(enm: EVOwnership, ownershipTypesArray: string[]) {
      let text: string = null;
      switch (enm) {
          case EVOwnership.Ukjent:
              break;
          case EVOwnership.Obl_leilighet:
              text = "obl leil";
              break;
          case EVOwnership.Aksjeleilighet:
              text = "aksjeleil";
              break;
          case EVOwnership.Borettslag:
              text = "andel borettsl";
              break;
          default:
              text = "selveier";
              break;
      }
      if (text != null) {
          ownershipTypesArray.push(text);
      }
  }

  private createGreenString(greenHousingType: GreenHomeBasisType): string {
      switch (greenHousingType) {
          case GreenHomeBasisType.Top15:
              return "Topp 15%";
          case GreenHomeBasisType.EnergyLabelA:
              return "Energimerke A";
          case GreenHomeBasisType.NZEB:
              return "NZEB-10%";
          default:
              return GreenHomeBasisType[greenHousingType].toLowerCase();
      }
  }
}
